import * as yup from 'yup';
import errorMessages, { supportedFileTypes } from "protocol-setup/constants/errorMessages";

const { regulatorySetupForm, infoForm } = errorMessages.regulatory;

const regulatorySetupSchema = yup.object({
  requestType: yup
    .string()
    .optional(),
  protocolVersionDt: yup
    .string()
    .required(regulatorySetupForm.protocolVersionDt.required),
  protocolVersionNumber: yup
    .string()
    .max(100, regulatorySetupForm.protocolVersionNumber.max)
    .required(regulatorySetupForm.protocolVersionNumber.required),
  levelIrbReviewRequired: yup
    .string()
    .required(regulatorySetupForm.levelIrbReviewRequired.required),
  ohrpCategory: yup
    .array()
    .of(yup.string())
    .when('levelIrbReviewRequired', ([levelIrbReviewRequired], schema) => {
      return levelIrbReviewRequired === 'Expedited'
        ? schema.min(1, regulatorySetupForm.ohrpCategory.required)
        : schema.optional();
    }),
  whenExpeditedReviewAllowed: yup
    .string()
    .when('levelIrbReviewRequired', ([levelIrbReviewRequired], schema) => {
      return levelIrbReviewRequired === 'Expedited'
        ? schema.required(regulatorySetupForm.whenExpeditedReviewAllowed.required)
        : schema.optional();
    }),
  releaseDt: yup
    .string()
    .required(regulatorySetupForm.releaseDt.required),
  ctsuCollectionRequired: yup
    .boolean(regulatorySetupForm.ctsuCollectionRequired.boolean)
    .transform((value) => {
      if (value === '' || value === null) {
        return undefined
      }
      return value
    })
    .when('requestType', ([requestType], schema) => {
      return requestType === 'AMENDMENT'
        ? schema.required(regulatorySetupForm.ctsuCollectionRequired.required)
        : schema.optional();
    }),
  anticipatedPrimaryCompletionDt: yup
    .string()
    .when('requestType', ([requestType], schema) => {
      return requestType !== 'AMENDMENT'
        ? schema.required(regulatorySetupForm.anticipatedPrimaryCompletionDt.required)
        : schema.optional();
    }),
  isCollectIrbApprovalPostClosure: yup
    .boolean(regulatorySetupForm.isCollectIrbApprovalPostClosure.boolean)
    .transform((value) => {
      if (value === '' || value === null) {
        return undefined
      }
      return value
    })
    .when('requestType', ([requestType], schema) => {
      return requestType !== 'AMENDMENT'
        ? schema.required(regulatorySetupForm.isCollectIrbApprovalPostClosure.required)
        : schema.optional();
    }),
  countries: yup
    .array()
    .of(yup.string())
    .min(1, regulatorySetupForm.countries.min)
    .required(regulatorySetupForm.countries.required),
  isCtaRequired: yup
    .boolean(regulatorySetupForm.isCtaRequired.boolean)
    .optional(),
  ctaRequiredInfo: yup
    .string()
    .max(100, regulatorySetupForm.ctaRequiredInfo.max)
    .when(['countries', 'isCtaRequired'], ([countries, isCtaRequired], schema) => {
      return Array.isArray(countries) &&
        countries.includes('Canada') &&
        isCtaRequired === true
        ? schema.required(regulatorySetupForm.ctaRequiredInfo.required)
        : schema.optional();
    }),
  participationChangeDesc: yup
    .string()
    .max(500, regulatorySetupForm.participationChangeDesc.max)
    .optional(),
  indAgent: yup
    .string()
    .max(100, regulatorySetupForm.indAgent.max)
    .when('indHolder', ([indHolder], schema) => {
      return indHolder === 'Non-CTEP Held IND'
        ? schema.required(regulatorySetupForm.indAgent.required)
        : schema.optional();
    }),
  indHolder: yup
    .string()
    .required(regulatorySetupForm.indHolder.required),
  indNumber: yup
    .string()
    .max(100, regulatorySetupForm.indNumber.max)
    .when('indHolder', ([indHolder], schema) => {
      return indHolder === 'Non-CTEP Held IND'
        ? schema.required(regulatorySetupForm.indNumber.required)
        : schema.optional();
    }),
  psrDetails: yup
    .array()
    .of(yup.object())
    .test(
      "checkPSRDuplicates",
      errorMessages.browser.protocolSetupForm.validation.psrDuplicate,
      (psrDetailsList) => {
        if (!psrDetailsList) return true;
        const uniqueDetails = new Set();
        return !psrDetailsList.some((item) => {
          const identifier = `${item.psrName}|${item.psrType}|${item.psrNumOfPersons}|${item.isEvaluationByGroup}|${item.sourceSubmittedBy}|${item.ctsuProcessingInstructions}|${item.requestedStatus}|${item.modifiedDt}`;
          if (uniqueDetails.has(identifier)) {
            return true;
          }
          uniqueDetails.add(identifier);
          return false;
        });
      }
    )
    .when('requestType', ([requestType], schema) => {
      return requestType === 'NEW_SUBMISSION'
        ? schema
            .min(1, regulatorySetupForm.psrDetails.min)
            .required(regulatorySetupForm.psrDetails.required)
        : schema.optional();
    }),
  attachPSR: yup
    .array()
    .test(
      "fileFormat",
      regulatorySetupForm.attachPSR.test,
      (files) => {
        if (!files || files.length === 0) return true;
        const supportedFormats = supportedFileTypes
        return files.every(
          (file) => !file || supportedFormats.includes(file.name?.split(".").pop().toLowerCase())
        );
      }
    )
    .optional()
});
export const InfoFormSchema = yup.object({
  requestType: yup
    .string()
    .required(infoForm.requestType.required),
  primaryContact: yup
    .array()
    .of(yup.number())
    .min(1, infoForm.primaryContact.min)
});
export default regulatorySetupSchema;
